/**
 * Assessment Session Recovery
 *
 * Persists in-progress assessment state to localStorage so that
 * a browser refresh during a 15-20 minute test doesn't lose all progress.
 *
 * Addresses: PRODUCT_UX_REPORT "Assessment state stored in React only"
 * (Confirmed Bug #5, Severity: 🔴 HIGH)
 *
 * Flow:
 * 1. On assessment start → saveSessionState()
 * 2. After each answer → saveSessionState() with updated answers
 * 3. On assessment complete/abandon → clearSessionState()
 * 4. On app mount → getRecoverableSession() to check for in-progress test
 * 5. User chooses resume → restore state; or abandon → clearSessionState()
 */

import { AssessmentAttempt, AssessmentBlueprint } from '../types'
import { STORAGE_KEY } from '../../../config/constants'
import { warn, error as logError, debug } from '../../../utils/logger'
import { safeStorageWrite } from '../../../utils/storageQuota'
import { FamilyMasteryState } from '../../../utils/familyMasteryEngine'

const SESSION_KEY = `${STORAGE_KEY}_assessment_session`

/** Maximum age for a recoverable session (2 hours) */
const MAX_SESSION_AGE_MS = 2 * 60 * 60 * 1000

export interface RecoverableSession {
  blueprint: AssessmentBlueprint
  attempt: AssessmentAttempt
  startTime: number
  preFamilyMastery: Record<string, FamilyMasteryState>
  savedAt: number
}

/**
 * Save the current assessment session state to localStorage.
 * Called after each answer to ensure progress is preserved.
 */
export function saveSessionState(
  blueprint: AssessmentBlueprint,
  attempt: AssessmentAttempt,
  startTime: number,
  preFamilyMastery: Record<string, FamilyMasteryState>,
): void {
  try {
    const session: RecoverableSession = {
      blueprint,
      attempt,
      startTime,
      preFamilyMastery,
      savedAt: Date.now(),
    }
    const success = safeStorageWrite(SESSION_KEY, JSON.stringify(session))
    if (!success) {
      warn('Failed to save assessment session — localStorage may be full')
    } else {
      debug(
        `Assessment session saved: ${attempt.answers.length}/${blueprint.questions.length} answers`,
      )
    }
  } catch (err) {
    logError('Failed to save assessment session state:', err)
  }
}

/**
 * Check for a recoverable in-progress assessment session.
 * Returns the session data if found and still valid, null otherwise.
 */
export function getRecoverableSession(): RecoverableSession | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY)
    if (!stored) return null

    const session: RecoverableSession = JSON.parse(stored)

    // Validate session age (discard if older than 2 hours)
    if (Date.now() - session.savedAt > MAX_SESSION_AGE_MS) {
      debug('Discarding stale assessment session (older than 2 hours)')
      clearSessionState()
      return null
    }

    // Validate session structure
    if (
      !session.blueprint ||
      !session.attempt ||
      !session.attempt.answers ||
      session.attempt.status !== 'in-progress'
    ) {
      debug('Discarding invalid assessment session')
      clearSessionState()
      return null
    }

    // Validate there are still questions to answer
    const answeredCount = session.attempt.answers.length
    const totalQuestions = session.blueprint.questions.length
    if (answeredCount >= totalQuestions) {
      debug('Assessment session is already complete — clearing')
      clearSessionState()
      return null
    }

    debug(
      `Recoverable assessment session found: ${answeredCount}/${totalQuestions} answers, saved ${Math.round((Date.now() - session.savedAt) / 60000)} minutes ago`,
    )
    return session
  } catch (err) {
    logError('Failed to read assessment session:', err)
    clearSessionState()
    return null
  }
}

/**
 * Clear the saved assessment session state.
 * Called when the assessment is completed, abandoned, or recovery is declined.
 */
export function clearSessionState(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
    debug('Assessment session state cleared')
  } catch (err) {
    logError('Failed to clear assessment session state:', err)
  }
}
